import React, { useState } from 'react';

const ROD_COLORS = [
  { bead: '#3B82F6', active: '#1D4ED8', hover: '#60A5FA', label: 'Ones' },
  { bead: '#10B981', active: '#059669', hover: '#34D399', label: 'Tens' },
  { bead: '#F59E0B', active: '#D97706', hover: '#FBBF24', label: 'Hundreds' },
  { bead: '#EF4444', active: '#DC2626', hover: '#F87171', label: 'Thousands' },
  { bead: '#8B5CF6', active: '#7C3AED', hover: '#A78BFA', label: 'Ten Thousands' },
];

// ─── Soroban helpers ───
const getSorobanBeadStates = (digitValue) => {
  const clamped = Math.min(9, Math.max(0, digitValue));
  return {
    heavenActive: clamped >= 5,
    earthActive: clamped % 5,
  };
};

// ─── Standard helpers ───
const getStandardActiveCount = (digitValue) => Math.min(9, Math.max(0, digitValue));

const AbacusSVG = ({
  value = [],
  onChange,
  numRods = 4,
  interactive = true,
  highlightRod = null,
  showLabels = true,
  style = 'soroban', // 'soroban' | 'standard'
}) => {
  const [hoveredBead, setHoveredBead] = useState(null); // 'rod-beadIdx' key

  // Ensure value array has correct length (rods are stored right-to-left: index 0 = ones)
  const paddedValue = Array.from({ length: numRods }, (_, i) => value[i] || 0);

  // Dimensions — larger touch targets on mobile via increased spacing
  const rodSpacing = 80;
  const svgWidth = numRods * rodSpacing + 40;
  const svgHeight = style === 'soroban' ? 340 : 420;
  const rodBaseX = (index) => (numRods - 1 - index) * rodSpacing + 60; // reverse so ones is rightmost

  const beadKey = (rodIndex, beadId) => `${rodIndex}-${beadId}`;

  // ─── Soroban click handlers ───
  const handleHeavenClick = (rodIndex) => {
    if (!interactive) return;
    const { heavenActive, earthActive } = getSorobanBeadStates(paddedValue[rodIndex]);
    const newVal = [...paddedValue];
    newVal[rodIndex] = (heavenActive ? 0 : 5) + earthActive;
    onChange(newVal);
  };

  const handleEarthClick = (rodIndex, beadIndex) => {
    if (!interactive) return;
    const { heavenActive, earthActive } = getSorobanBeadStates(paddedValue[rodIndex]);
    let newEarthActive;
    if (beadIndex < earthActive) {
      // clicking an active bead — deactivate it and those below
      newEarthActive = beadIndex;
    } else {
      // clicking an inactive bead — activate it and those above
      newEarthActive = beadIndex + 1;
    }
    const newVal = [...paddedValue];
    newVal[rodIndex] = (heavenActive ? 5 : 0) + newEarthActive;
    onChange(newVal);
  };

  // ─── Standard click handler ───
  const handleStandardClick = (rodIndex, beadIndex) => {
    if (!interactive) return;
    const currentActive = getStandardActiveCount(paddedValue[rodIndex]);
    let newActive;
    if (beadIndex < currentActive) {
      newActive = beadIndex;
    } else {
      newActive = beadIndex + 1;
    }
    const newVal = [...paddedValue];
    newVal[rodIndex] = newActive;
    onChange(newVal);
  };

  // ─── SVG filter for hover glow ───
  const renderDefs = () => (
    <defs>
      <filter id="bead-glow" x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur stdDeviation="3" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
  );

  // ─── Render Soroban ───
  const renderSoroban = () => {
    const beamY = 110;
    const heavenTop = 30;
    const heavenSpacing = 55;
    const earthTop = beamY + 30;
    const earthSpacing = 42;
    const beadRx = 28;
    const beadRy = 16;

    return (
      <>
        {renderDefs()}

        {/* Frame */}
        <rect x="15" y="10" width={svgWidth - 30} height={svgHeight - 40} rx="12" ry="12"
          fill="#8B6914" stroke="#6B4F10" strokeWidth="3" />
        <rect x="22" y="17" width={svgWidth - 44} height={svgHeight - 54} rx="8" ry="8"
          fill="#FDF6E3" />

        {/* Beam */}
        <rect x="22" y={beamY - 5} width={svgWidth - 44} height="10" fill="#A0781C" />

        {/* Rods and beads */}
        {paddedValue.map((digit, rodIndex) => {
          const x = rodBaseX(rodIndex);
          const color = ROD_COLORS[rodIndex] || ROD_COLORS[0];
          const { heavenActive, earthActive } = getSorobanBeadStates(digit);
          const isHighlighted = highlightRod === rodIndex;

          // Heaven bead y position
          const heavenY = heavenActive ? beamY - heavenSpacing + 20 : heavenTop;
          const heavenKey = beadKey(rodIndex, 'h');
          const isHeavenHovered = hoveredBead === heavenKey;

          return (
            <g key={`rod-${rodIndex}`} role="group" aria-label={`${color.label} rod, value ${digit}`}>
              {/* Rod */}
              <line x1={x} y1="20" x2={x} y2={svgHeight - 45}
                stroke="#C4A94A" strokeWidth="3" />

              {/* Highlight glow */}
              {isHighlighted && (
                <rect x={x - 34} y="20" width="68" height={svgHeight - 65}
                  rx="6" fill="rgba(28, 183, 200, 0.12)" stroke="#1CB7C8" strokeWidth="1.5" strokeDasharray="4" />
              )}

              {/* Heaven bead */}
              <ellipse
                cx={x} cy={heavenY}
                rx={isHeavenHovered ? beadRx + 2 : beadRx}
                ry={isHeavenHovered ? beadRy + 1 : beadRy}
                fill={isHeavenHovered ? color.hover : (heavenActive ? color.active : color.bead)}
                stroke={heavenActive ? '#fff' : '#00000020'}
                strokeWidth={heavenActive ? 2 : 1}
                filter={isHeavenHovered ? 'url(#bead-glow)' : undefined}
                onClick={() => handleHeavenClick(rodIndex)}
                onMouseEnter={() => interactive && setHoveredBead(heavenKey)}
                onMouseLeave={() => setHoveredBead(null)}
                role="button"
                tabIndex={interactive ? 0 : -1}
                aria-label={`Heaven bead, value 5, ${heavenActive ? 'active' : 'inactive'}. Click to ${heavenActive ? 'deactivate' : 'activate'}.`}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleHeavenClick(rodIndex); }}}
                style={{ cursor: interactive ? 'pointer' : 'default', transition: 'all 0.18s ease-out' }}
              />
              {/* "5" label on heaven bead */}
              <text x={x} y={heavenY + 5} textAnchor="middle" fontSize="11"
                fontWeight="bold" fill="#fff" pointerEvents="none">5</text>

              {/* Earth beads */}
              {[0, 1, 2, 3].map((beadIdx) => {
                const isActive = beadIdx < earthActive;
                const eKey = beadKey(rodIndex, `e${beadIdx}`);
                const isEarthHovered = hoveredBead === eKey;
                // Active beads sit close to beam, inactive ones sit low
                const beadY = isActive
                  ? earthTop + (earthActive - 1 - beadIdx) * earthSpacing
                  : earthTop + (3 - beadIdx) * earthSpacing + 20;

                return (
                  <g key={`earth-${beadIdx}`}>
                    <ellipse
                      cx={x} cy={beadY}
                      rx={isEarthHovered ? beadRx + 2 : beadRx}
                      ry={isEarthHovered ? beadRy + 1 : beadRy}
                      fill={isEarthHovered ? color.hover : (isActive ? color.active : color.bead)}
                      stroke={isActive ? '#fff' : '#00000020'}
                      strokeWidth={isActive ? 2 : 1}
                      filter={isEarthHovered ? 'url(#bead-glow)' : undefined}
                      onClick={() => handleEarthClick(rodIndex, beadIdx)}
                      onMouseEnter={() => interactive && setHoveredBead(eKey)}
                      onMouseLeave={() => setHoveredBead(null)}
                      role="button"
                      tabIndex={interactive ? 0 : -1}
                      aria-label={`Earth bead ${beadIdx + 1}, value 1, ${isActive ? 'active' : 'inactive'}`}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleEarthClick(rodIndex, beadIdx); }}}
                      style={{ cursor: interactive ? 'pointer' : 'default', transition: 'all 0.18s ease-out' }}
                    />
                    <text x={x} y={beadY + 5} textAnchor="middle" fontSize="11"
                      fontWeight="bold" fill="#fff" pointerEvents="none">1</text>
                  </g>
                );
              })}

              {/* Place value label */}
              {showLabels && (
                <text x={x} y={svgHeight - 10} textAnchor="middle"
                  fontSize="12" fontWeight="600" fill="#6B7280">
                  {color.label}
                </text>
              )}
            </g>
          );
        })}
      </>
    );
  };

  // ─── Render Standard (10 beads per rod) ───
  const renderStandard = () => {
    const topMargin = 30;
    const beadSpacing = 34;
    const beadRx = 28;
    const beadRy = 14;

    return (
      <>
        {renderDefs()}

        {/* Frame */}
        <rect x="15" y="10" width={svgWidth - 30} height={svgHeight - 40} rx="12" ry="12"
          fill="#8B6914" stroke="#6B4F10" strokeWidth="3" />
        <rect x="22" y="17" width={svgWidth - 44} height={svgHeight - 54} rx="8" ry="8"
          fill="#FDF6E3" />

        {/* Rods and beads */}
        {paddedValue.map((digit, rodIndex) => {
          const x = rodBaseX(rodIndex);
          const color = ROD_COLORS[rodIndex] || ROD_COLORS[0];
          const activeCount = getStandardActiveCount(digit);
          const isHighlighted = highlightRod === rodIndex;

          return (
            <g key={`rod-${rodIndex}`} role="group" aria-label={`${color.label} rod, value ${digit}`}>
              {/* Rod */}
              <line x1={x} y1="20" x2={x} y2={svgHeight - 45}
                stroke="#C4A94A" strokeWidth="3" />

              {/* Highlight */}
              {isHighlighted && (
                <rect x={x - 34} y="20" width="68" height={svgHeight - 65}
                  rx="6" fill="rgba(28, 183, 200, 0.12)" stroke="#1CB7C8" strokeWidth="1.5" strokeDasharray="4" />
              )}

              {/* 10 beads — active ones sit at the top, inactive at the bottom */}
              {Array.from({ length: 10 }, (_, beadIdx) => {
                const isActive = beadIdx < activeCount;
                const sKey = beadKey(rodIndex, `s${beadIdx}`);
                const isStdHovered = hoveredBead === sKey;
                // Active beads stack from top down, inactive from bottom up
                const beadY = isActive
                  ? topMargin + beadIdx * beadSpacing
                  : topMargin + (9 - (9 - beadIdx)) * beadSpacing;

                return (
                  <ellipse
                    key={`std-${beadIdx}`}
                    cx={x} cy={beadY}
                    rx={isStdHovered ? beadRx + 2 : beadRx}
                    ry={isStdHovered ? beadRy + 1 : beadRy}
                    fill={isStdHovered ? color.hover : (isActive ? color.active : color.bead)}
                    stroke={isActive ? '#fff' : '#00000020'}
                    strokeWidth={isActive ? 2 : 1}
                    filter={isStdHovered ? 'url(#bead-glow)' : undefined}
                    onClick={() => handleStandardClick(rodIndex, beadIdx)}
                    onMouseEnter={() => interactive && setHoveredBead(sKey)}
                    onMouseLeave={() => setHoveredBead(null)}
                    role="button"
                    tabIndex={interactive ? 0 : -1}
                    aria-label={`Bead ${beadIdx + 1}, ${isActive ? 'active' : 'inactive'}`}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleStandardClick(rodIndex, beadIdx); }}}
                    style={{ cursor: interactive ? 'pointer' : 'default', transition: 'all 0.18s ease-out' }}
                  />
                );
              })}

              {/* Place value label */}
              {showLabels && (
                <text x={x} y={svgHeight - 10} textAnchor="middle"
                  fontSize="12" fontWeight="600" fill="#6B7280">
                  {color.label}
                </text>
              )}
            </g>
          );
        })}
      </>
    );
  };

  // Compute displayed number for aria-label
  const displayedNumber = paddedValue.reduce((sum, d, i) => sum + d * Math.pow(10, i), 0);

  return (
    <svg
      viewBox={`0 0 ${svgWidth} ${svgHeight}`}
      className="w-full max-w-lg mx-auto select-none"
      style={{ touchAction: 'manipulation' }}
      role="application"
      aria-label={`${style === 'soroban' ? 'Soroban' : 'Standard'} abacus showing ${displayedNumber}. Click beads to change values.`}
    >
      {style === 'soroban' ? renderSoroban() : renderStandard()}
    </svg>
  );
};

export default AbacusSVG;
